import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { formatApiError, useAuth } from "@/context/AuthContext";
import { Kicker, Reveal } from "@/components/Motion";

export default function AdminLogin() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.role !== "admin") {
        await logout();
        setError("This account does not have admin access.");
        return;
      }
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-4 py-16" data-testid="admin-login-page">
      <Reveal className="w-full max-w-md">
        <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm sm:p-10">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-50 text-blue-900">
            <ShieldCheck className="h-6 w-6" aria-hidden="true" />
          </span>
          <Kicker className="mt-6">Restricted Area</Kicker>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900">
            Admin Login
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            NUC Service — National Union Corporation. Authorised administrators only.
          </p>
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-login-email" className="field-label">Admin email</label>
              <input
                id="admin-login-email"
                type="email"
                data-testid="admin-login-email-input"
                className="field-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="admin@nucservice.com"
              />
            </div>
            <div>
              <label htmlFor="admin-login-password" className="field-label">Password</label>
              <input
                id="admin-login-password"
                type="password"
                data-testid="admin-login-password-input"
                className="field-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p
                data-testid="admin-login-error"
                className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit-button"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-900 px-6 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? "Verifying…" : "Sign in to dashboard"}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
        </div>
      </Reveal>
    </div>
  );
}
